import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { GetInventory$Params } from 'src/api/generated/fn/inventory/get-inventory';
import { GetStockTransfer$Params } from 'src/api/generated/fn/inventory/get-stock-transfer';
import { ReceiveStockTransfer$Params } from 'src/api/generated/fn/inventory/receive-stock-transfer';
import { InventoryDto } from 'src/api/generated/models/Digiwork/MaitreD/Pos/DTO/Inventory/inventory-dto';
import { StockTransferLineItemDto } from 'src/api/generated/models/Digiwork/MaitreD/Pos/DTO/InventoryStockTake/stock-transfer-line-item-dto';
import { StockTransferLineItemsDto } from 'src/api/generated/models/Digiwork/MaitreD/Pos/DTO/InventoryStockTransferLineItems/stock-transfer-line-items-dto';
import { InventoryStockTransferDto } from 'src/api/generated/models/Digiwork/MaitreD/Pos/DTO/InventoryStockTransfers/inventory-stock-transfer-dto';
import { InventoryService } from 'src/api/generated/services';
import { InventoryActionStatus } from 'src/app/pos-panel/enums/inventory-action-status.enum';
import { CurrentVenueHelperService } from 'src/app/share-modul/services/current-venue-helper/current-venue.helper.service';

@Component({
  selector: 'app-transfer-receipt',
  templateUrl: './transfer-receipt.component.html',
  styleUrls: ['./transfer-receipt.component.scss']
})
export class TransferReceiptComponent implements OnInit, OnDestroy {
  
  venueId: string;
  inventoryId: string;
  transferId: string;
  venueIdUpdateSubscription: Subscription;
  inventory: InventoryDto;

  transfer: InventoryStockTransferDto = {} as InventoryStockTransferDto;
  receivedItems: Array<StockTransferLineItemDto> = new Array<StockTransferLineItemDto>();
  InventoryActionStatus = InventoryActionStatus;
  form: FormGroup;
  timezoneOffset: any;

  constructor(private router: Router,
              private formBuilder: FormBuilder,
              private route: ActivatedRoute,
              private inventoryService: InventoryService,
              private confirmationService: ConfirmationService, 
              public translate: TranslateService,
              private currentVenueHelper: CurrentVenueHelperService,
              private messageService: MessageService) { }

  ngOnInit(): void {
    this.form = new FormGroup({
      items: new FormArray([])
    });   
    this.route.params.subscribe(params => {
      if (params['venue-id'] != null) {
        this.venueId = params['venue-id'];
        this.currentVenueHelper.updateCurrentVenueIdDropdown(params['venue-id']);
        this.currentVenueHelper.getTimezone().subscribe(tzOffset => this.timezoneOffset = tzOffset);
      }
      if (params['inventory-id'] != null && params['transfer-id'] != null) {
        this.inventoryId = params['inventory-id'];
        this.transferId = params['transfer-id'];

        const inventoryParams: GetInventory$Params = { inventoryId: this.inventoryId };
        this.inventoryService.getInventory(inventoryParams).subscribe(response => this.inventory = response);

        const transferParams: GetStockTransfer$Params = { inventoryId: this.inventoryId, transferId: this.transferId };
        this.inventoryService.getStockTransfer(transferParams).subscribe(response => {
          this.transfer = response;
          this.receivedItems = this.transfer.lineItems ? this.transfer.lineItems?.filter(item => item.quantityReceived != null ) : [];
          this.createForm();
        });
      }
    });
  }

  private createForm(): void {
    this.form = new FormGroup({
      items: this.formBuilder.array([])
    });   
    this.transfer.lineItems?.forEach(item => this.items.push(this.createTableRow()))
  }

  get items(): FormArray {
    return this.form.get('items') as FormArray;
  }
  
  getItemControls(index: number){
    return (this.items.controls[index] as FormGroup)
  }

  private createTableRow(): FormGroup {
    return this.formBuilder.group({
      item: new FormControl('', {
        validators: []
      }),
     quantityReceived: new FormControl('', {
        validators: []
      })
    });
  }

  selectRow(event: any){
    let index = event.index;

    (this.items.controls[index] as FormGroup).controls['item'].setValue(event.data.item);
    (this.items.controls[index] as FormGroup).controls['item'].updateValueAndValidity();

    (this.items.controls[index] as FormGroup).controls['quantityReceived'].setValidators(Validators.required)
    this.getItemControls(index).controls['quantityReceived'].updateValueAndValidity();

  }

  unselectRow(event: any){
    let index = event.index;

    this.getItemControls(index).controls['quantityReceived'].removeValidators(Validators.required);
    this.getItemControls(index).controls['quantityReceived'].updateValueAndValidity();
  }


  receiveTransfer(){
    if(this.form.valid){
      this.confirmationService.confirm({
        header: this.translate.instant('TRANSFER.CONFIRMATION_DIALOG_RECEIVE_TRANSFER_TITLE'),
        message: this.translate.instant('TRANSFER.CONFIRMATION_DIALOG_RECEIVE_TRANSFER_QUESTION'),
        acceptLabel: this.translate.instant('YES'),
        rejectLabel: this.translate.instant('NO'),
        defaultFocus: "reject",
        acceptButtonStyleClass: 'soc-confirmation-dialog dialog-btn constructive-btn',
        rejectButtonStyleClass: 'soc-confirmation-dialog dialog-btn reject-btn',
        accept: () => {
          const selectedItems = this.receivedItems.map(item => item.item?.id);
          const formItems = this.form.getRawValue().items.filter((item: any) => selectedItems.indexOf(item.item.id) != -1);
          const items: StockTransferLineItemsDto = { lineItems: formItems };
          var params:  ReceiveStockTransfer$Params = {} as ReceiveStockTransfer$Params;
          params.transferId = this.transferId;
          params.body = items ;
          if (this.transfer.sourceVenueInventory?.id != undefined) {
            params.inventoryId = this.transfer.sourceVenueInventory.id;
          }
          this.inventoryService.receiveStockTransfer(params).subscribe({
            next: response => {
              this.messageService.add( { severity: 'success', summary: 'Transfer was successfully recieved!', life: 3000 } )
              this.navigateToMain();
            },
            error: errorResponse => this.messageService.add( { severity: 'error', summary: 'Error occurred!', life: 3000 } )
          });     
        }
      });
  }
  }

  validateNumOfSelectedItems(){
    return this.form.getRawValue().items.filter((item: any) => item.quantityReceived != '' && item.quantityReceived != null).length;
  }

  navigateToMain(){
    if(this.inventory.isMain){
      this.router.navigate([this.venueId, "inventory", this.inventoryId], { queryParams: { activeIndex: 3 }}); 
    }else{
      this.router.navigate([this.venueId, "inventory", this.inventoryId], { queryParams: { activeIndex: 2 }}); 
    }
  }
  public ngOnDestroy(): void {
    if (this.venueIdUpdateSubscription) {
      this.venueIdUpdateSubscription.unsubscribe();
    }
  }

}
